import React, { useState } from 'react'
import { FaGavel, FaFileAlt, FaLightbulb, FaChartLine, FaExclamationTriangle, FaCheckCircle, FaPlus, FaClock, FaEdit, FaDownload } from 'react-icons/fa'
import { exportOfficeActionAnalysis } from '../util/docx-export'

const OfficeActionAnalysisDisplay = ({ result, theme = 'light', searchTerm = '', matchesWildcard, showToggle = false, onAddToInput, onCopyResult, showToast, addToResources, contextData = {} }) => {
  const [showStructured, setShowStructured] = useState(false)

  // Parse result if it's a string (using same logic as OfficeActionExtractDisplay)
  const parseResult = (data) => {
    if (typeof data === 'string') {
      try {
        // Try to fix common JSON formatting issues
        let jsonString = data
        
        // Replace single quotes with double quotes for proper JSON
        if (jsonString.startsWith("'") && jsonString.endsWith("'")) {
          jsonString = jsonString.slice(1, -1) // Remove outer quotes
        }
        
        // Try to parse as JSON
        let parsedResult
        try {
          parsedResult = JSON.parse(jsonString)
        } catch (firstError) {
          // If that fails, try replacing single quotes with double quotes
          // Handle nested single quotes more carefully
          let fixedJsonString = jsonString
          
          // Replace single quotes with double quotes, but be careful about apostrophes in strings
          // This is a more robust approach for the {'output': ...} format
          try {
            // Handle mixed single/double quotes - values with apostrophes use double quotes
            let fixedJsonString = jsonString
            
            // First pass: find all string values that are already in double quotes (contain apostrophes)
            // and temporarily replace them with placeholders
            const doubleQuotedStrings = []
            let placeholder = 0
            
            // Match strings that are already in double quotes: "text with ' apostrophe"
            fixedJsonString = fixedJsonString.replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, (match, content) => {
              const placeholderKey = `__PLACEHOLDER_${placeholder++}__`
              doubleQuotedStrings.push({ key: placeholderKey, value: match })
              return placeholderKey
            })
            
            // Now convert all remaining single quotes to double quotes
            fixedJsonString = fixedJsonString.replace(/'/g, '"')
            
            // Restore the original double-quoted strings
            doubleQuotedStrings.forEach(item => {
              fixedJsonString = fixedJsonString.replace(item.key, item.value)
            })
            
            // Clean up Python-style values
            fixedJsonString = fixedJsonString
              .replace(/True/g, 'true')
              .replace(/False/g, 'false')
              .replace(/None/g, 'null')
            
            parsedResult = JSON.parse(fixedJsonString)
          } catch (secondError) {
            // If JSON still fails, try a more aggressive approach for Python-style dicts
            try {
              // Handle Python-style boolean/null values
              fixedJsonString = jsonString
                .replace(/'/g, '"')
                .replace(/True/g, 'true')
                .replace(/False/g, 'false')
                .replace(/None/g, 'null')
              parsedResult = JSON.parse(fixedJsonString)
            } catch (thirdError) {
              throw thirdError
            }
          }
        }
        
        // If we have an 'output' property, extract it
        if (parsedResult && typeof parsedResult === 'object' && parsedResult.output) {
          return parsedResult.output
        }
        
        return parsedResult
      } catch (e) {
        // If parsing fails, return original data
        console.warn('Failed to parse office action analysis result as JSON:', e.message)
        return data
      }
    }
    
    // If it's already an object, check if we need to extract 'output'
    if (data && typeof data === 'object' && data.output) {
      return data.output
    }
    
    return data
  }

  // Parse the result first
  const parsedResult = parseResult(result)

  // Check if the result looks like an office action analysis result
  const isOfficeActionAnalysisResult = (data) => {
    if (!data || typeof data !== 'object') return false
    
    // Check for key office action analysis properties
    const hasOAAnalysisProperties = data.claim_analysis || data.rejection_analysis || 
                                   data.prior_art_analysis || data.response_strategy ||
                                   data.amendment_suggestions || data.argument_points
    
    return !!hasOAAnalysisProperties
  }

  if (!isOfficeActionAnalysisResult(parsedResult)) {
    return null // Not an office action analysis result, don't render this component
  }

  // Function to extract patent/publication info from reference text
  const extractPatentInfo = (referenceText) => {
    if (!referenceText) return null
    
    // Publication pattern - US followed by YYYY0XXXXXX (year + 0 + digits)
    const publicationPattern = /US\s*(\d{4}0\d{6})/gi
    
    // Patent pattern - shorter numbers like 10123456 or 10,123,456
    const patentPattern = /US\s*(\d{1,2}[,\s]*\d{3}[,\s]*\d{3})/gi
    
    // Check for publication first (more specific pattern)
    const pubMatch = referenceText.match(publicationPattern)
    if (pubMatch) {
      const pubNumber = pubMatch[0].replace(/US\s*/gi, '').replace(/[,\s]/g, '')
      return { type: 'publication', id: pubNumber, original: pubMatch[0] }
    }
    
    // Check for patent
    const patentMatch = referenceText.match(patentPattern)
    if (patentMatch) {
      const patentNumber = patentMatch[0].replace(/US\s*/gi, '').replace(/[,\s]/g, '')
      return { type: 'patent', id: patentNumber, original: patentMatch[0] }
    }
    
    return null
  }

  // Function to check if patent/publication is already in session
  const isAlreadyInSession = (patentInfo) => {
    if (!patentInfo || !contextData) return false
    const context: any = contextData;

    if (patentInfo.type === 'patent') {
      return context.current_patents && context.current_patents.includes(patentInfo.id)
    } else if (patentInfo.type === 'publication') {
      return context.current_publications && context.current_publications.includes(patentInfo.id)
    }
    
    return false
  }

  // Function to handle adding patent/publication to session
  const handleAddToSession = (patentInfo) => {
    if (!addToResources || !patentInfo) return
    
    // Format the resource object like ResourceSelectorTool does for backend onboarding
    const resource = {
      type: patentInfo.type,
      id: patentInfo.id,
      file_name: patentInfo.id,
      file_type: patentInfo.type
    }
    
    try {
      // This will trigger the same backend onboarding as ResourceSelectorTool
      addToResources([resource])
      if (showToast) {
        showToast(`${patentInfo.type === 'patent' ? 'Patent' : 'Publication'} ${patentInfo.id} added to session`, 'success')
      }
    } catch (error) {
      console.error('Error adding to session:', error)
      if (showToast) {
        showToast(`Failed to add ${patentInfo.type} to session`, 'error')
      }
    }
  }

  // Helper function to render add to session button for prior art
  const renderPriorArtButton = (referenceName) => {
    if (!addToResources) return null
    
    // First try to extract patent info directly from the reference name
    let patentInfo = extractPatentInfo(referenceName)
    
    // If not found in name, check if we can find it in prior_art_analysis and try to infer from date
    if (!patentInfo && parsedResult.prior_art_analysis && parsedResult.prior_art_analysis[referenceName]) {
      const priorArtData = parsedResult.prior_art_analysis[referenceName]
      if (priorArtData.date) {
        // Try to infer patent/publication type from date format
        const dateStr = priorArtData.date
        
        // Look for common patent date formats and try to map to known references
        // This is a heuristic approach - in practice you might need a lookup table
        const year = dateStr.match(/(\d{4})/)
        if (year) {
          const yearNum = parseInt(year[1])
          
          // From your examples, we know:
          // Kerfeld is US 20200139546 A1 (2020)
          // Hashiguchi is US 20150045949 A1 (2015)
          if (referenceName.toLowerCase().includes('kerfeld') && yearNum === 2020) {
            patentInfo = { type: 'publication', id: '20200139546', original: 'US 20200139546' }
          } else if (referenceName.toLowerCase().includes('hashiguchi') && yearNum === 2015) {
            patentInfo = { type: 'publication', id: '20150045949', original: 'US 20150045949' }
          }
          // Add more mappings as needed based on your known prior art references
        }
      }
    }
    
    if (!patentInfo) return null
    
    const alreadyInSession = isAlreadyInSession(patentInfo)
    
    return (
      <div className="mt-2">
        {!alreadyInSession ? (
          <button
            onClick={() => handleAddToSession(patentInfo)}
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors
              ${theme === 'dark' 
                ? 'bg-green-800 hover:bg-green-700 text-green-100 border border-green-700' 
                : 'bg-green-100 hover:bg-green-200 text-green-800 border border-green-300'
              }`}
            title={`Add ${patentInfo.type} ${patentInfo.id} to session`}
          >
            <FaPlus className="w-3 h-3" />
            Add to Session
          </button>
        ) : (
          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md
            ${theme === 'dark' 
              ? 'bg-gray-700 text-gray-300 border border-gray-600' 
              : 'bg-gray-200 text-gray-600 border border-gray-300'
            }`}
            title={`${patentInfo.type} ${patentInfo.id} is already in session`}
          >
            <FaCheckCircle className="w-3 h-3" />
            Already in Session
          </span>
        )}
      </div>
    )
  }

  const formatStrengthColor = (strength) => {
    const strengthLower = strength?.toLowerCase()
    if (strengthLower === 'high') return theme === 'dark' ? 'text-red-300' : 'text-red-700'
    if (strengthLower === 'medium') return theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'
    if (strengthLower === 'low') return theme === 'dark' ? 'text-green-300' : 'text-green-700'
    return theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  }

  // Helper function to clean up author names
  const cleanAuthorName = (name) => {
    if (!name || typeof name !== 'string') return name

    // Remove underscores and replace with spaces
    let cleanedName = name.replace(/_/g, ' ')

    // Handle "et al" formatting - add period after "al" if it ends with "et al"
    cleanedName = cleanedName.replace(/\bet al\b(?!\.)/gi, 'et al.')

    return cleanedName
  }

  return (
    <div className={`w-full max-w-6xl mx-auto p-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      {showToggle && (
        <div className="mb-4 flex justify-between items-center">
          <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Office Action Analysis Results
          </h3>
          <div className="flex items-center gap-2">
            {onAddToInput && (
              <button
                onClick={() => {
                  onAddToInput(typeof parsedResult === 'object' ? JSON.stringify(parsedResult, null, 2) : parsedResult);
                  if (showToast) showToast('Result added to chat input!');
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                Add to Chat
              </button>
            )}
            <button
              onClick={() => exportOfficeActionAnalysis(parsedResult, showToast)}
              className={`text-xs py-1 px-3 rounded-md transition-colors flex items-center gap-1 ${
                theme === 'dark' ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-green-500 text-white hover:bg-green-600'
              }`}
              title="Export office action analysis as Word document"
            >
              <FaDownload />
              Export DOCX
            </button>
            {onCopyResult && (
              <button
                onClick={() => {
                  if (onCopyResult) onCopyResult();
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Copy
              </button>
            )}
            <button
              onClick={() => setShowStructured(!showStructured)}
              className={`px-3 py-1 text-sm rounded-md border ${
                theme === 'dark'
                  ? 'border-gray-600 hover:bg-gray-700 text-gray-300'
                  : 'border-gray-300 hover:bg-gray-100 text-gray-700'
              }`}
            >
              {showStructured ? 'Show Formatted' : 'Show Raw Data'}
            </button>
          </div>
        </div>
      )}

      {showStructured ? (
        <pre className={`text-xs whitespace-pre-wrap p-4 rounded-lg border overflow-auto max-h-96 ${
          theme === 'dark' ? 'bg-gray-800 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-800'
        }`}>
          {JSON.stringify(parsedResult, null, 2)}
        </pre>
      ) : (
        <div className="space-y-6">

          {/* Claims Status Summary */}
          {(parsedResult.claims_allowed || parsedResult.allowable_claims || parsedResult.claims_objected_to || parsedResult.claims_rejected) && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaGavel />
                Claims Status Summary
              </div>
              <div className="space-y-3">

                {/* Allowed Claims */}
                {parsedResult.claims_allowed && parsedResult.claims_allowed.length > 0 && (
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-green-900 bg-opacity-20 border-green-800' : 'bg-green-50 border-green-200'} border`}>
                    <div className={`font-medium mb-2 flex items-center gap-2 ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                      <FaCheckCircle />
                      Claims Allowed ({parsedResult.claims_allowed.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {parsedResult.claims_allowed.map((claimNum) => (
                        <span
                          key={claimNum}
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            theme === 'dark'
                              ? 'bg-green-800 text-green-100'
                              : 'bg-green-200 text-green-900'
                          }`}
                        >
                          {claimNum}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Allowable Claims */}
                {parsedResult.allowable_claims && parsedResult.allowable_claims.length > 0 && (
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-blue-900 bg-opacity-20 border-blue-800' : 'bg-blue-50 border-blue-200'} border`}>
                    <div className={`font-medium mb-2 flex items-center gap-2 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                      <FaCheckCircle />
                      Allowable Claims ({parsedResult.allowable_claims.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {parsedResult.allowable_claims.map((claimNum) => (
                        <span
                          key={claimNum}
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            theme === 'dark'
                              ? 'bg-blue-800 text-blue-100'
                              : 'bg-blue-200 text-blue-900'
                          }`}
                        >
                          {claimNum}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Objected Claims */}
                {parsedResult.claims_objected_to && parsedResult.claims_objected_to.length > 0 && (
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-yellow-900 bg-opacity-20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'} border`}>
                    <div className={`font-medium mb-2 flex items-center gap-2 ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'}`}>
                      <FaExclamationTriangle />
                      Claims Objected To ({parsedResult.claims_objected_to.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {parsedResult.claims_objected_to.map((claimNum) => (
                        <span
                          key={claimNum}
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            theme === 'dark'
                              ? 'bg-yellow-800 text-yellow-100'
                              : 'bg-yellow-200 text-yellow-900'
                          }`}
                        >
                          {claimNum}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejected Claims */}
                {parsedResult.claims_rejected && parsedResult.claims_rejected.length > 0 && (
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-red-900 bg-opacity-20 border-red-800' : 'bg-red-50 border-red-200'} border`}>
                    <div className={`font-medium mb-2 flex items-center gap-2 ${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>
                      <FaExclamationTriangle />
                      Claims Rejected ({parsedResult.claims_rejected.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {parsedResult.claims_rejected.map((claimNum) => (
                        <span
                          key={claimNum}
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            theme === 'dark'
                              ? 'bg-red-800 text-red-100'
                              : 'bg-red-200 text-red-900'
                          }`}
                        >
                          {claimNum}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Drawings Objections */}
          {parsedResult.drawings_objected && parsedResult.drawings_objection_details && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaExclamationTriangle />
                Drawings Objections
              </div>
              {parsedResult.drawings_objection_details.figures_affected && (
                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <strong>Figures Affected:</strong> {parsedResult.drawings_objection_details.figures_affected.join(', ')}
                </div>
              )}
              {parsedResult.drawings_objection_details.reasons && parsedResult.drawings_objection_details.reasons.length > 0 && (
                <div className="space-y-3">
                  {parsedResult.drawings_objection_details.reasons.map((reason, idx) => (
                    <div key={idx} className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-orange-900 bg-opacity-20 border-orange-800' : 'bg-orange-50 border-orange-200'} border`}>
                      <div className={`font-medium mb-2 ${theme === 'dark' ? 'text-orange-300' : 'text-orange-700'}`}>
                        {reason.figure}
                      </div>
                      <div className={`text-sm mb-1 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                        <strong>Issue:</strong> {reason.issue}
                      </div>
                      {reason.citation && (
                        <div className={`text-xs ${theme === 'dark' ? 'text-orange-300' : 'text-orange-600'}`}>
                          <strong>Citation:</strong> {reason.citation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Specification Objections */}
          {parsedResult.specification_objected && parsedResult.specification_objection_details && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaExclamationTriangle />
                Specification Objections
              </div>
              {parsedResult.specification_objection_details.portions_affected && (
                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <strong>Portions Affected:</strong> {parsedResult.specification_objection_details.portions_affected.join(', ')}
                </div>
              )}
              {parsedResult.specification_objection_details.reasons && parsedResult.specification_objection_details.reasons.length > 0 && (
                <div className="space-y-3">
                  {parsedResult.specification_objection_details.reasons.map((reason, idx) => (
                    <div key={idx} className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-orange-900 bg-opacity-20 border-orange-800' : 'bg-orange-50 border-orange-200'} border`}>
                      <div className={`font-medium mb-2 ${theme === 'dark' ? 'text-orange-300' : 'text-orange-700'}`}>
                        {reason.portion}
                      </div>
                      <div className={`text-sm mb-1 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                        <strong>Issue:</strong> {reason.issue}
                      </div>
                      {reason.citation && (
                        <div className={`text-xs ${theme === 'dark' ? 'text-orange-300' : 'text-orange-600'}`}>
                          <strong>Citation:</strong> {reason.citation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Abstract Objections */}
          {parsedResult.abstract_objected && parsedResult.abstract_objection_details && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaExclamationTriangle />
                Abstract Objections
              </div>
              {typeof parsedResult.abstract_objection_details === 'string' ? (
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {parsedResult.abstract_objection_details}
                </div>
              ) : (
                <div className="space-y-3">
                  {parsedResult.abstract_objection_details.issue && (
                    <div className={`text-sm mb-1 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                      <strong>Issue:</strong> {parsedResult.abstract_objection_details.issue}
                    </div>
                  )}
                  {parsedResult.abstract_objection_details.citation && (
                    <div className={`text-xs ${theme === 'dark' ? 'text-orange-300' : 'text-orange-600'}`}>
                      <strong>Citation:</strong> {parsedResult.abstract_objection_details.citation}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Claim Analysis */}
          {parsedResult.claim_analysis && Object.keys(parsedResult.claim_analysis).length > 0 && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaGavel />
                Claim Analysis
              </div>
              <div className="space-y-4">
                {Object.entries(parsedResult.claim_analysis)
                  .sort(([a], [b]) => {
                    // Extract numeric part from claim keys like "claim_1", "claim_2", etc.
                    const numA = parseInt(a.replace(/\D/g, '')) || 0;
                    const numB = parseInt(b.replace(/\D/g, '')) || 0;
                    return numA - numB;
                  })
                  .map(([claimNum, claimData]: [string, any]) => {
                  // Determine if claim is allowed, allowable, or rejected/objected
                  const status = claimData.status?.toLowerCase()
                  const isAllowed = status === 'allowed'
                  const isAllowable = status === 'allowable'
                  const isRejectedOrObjected = !isAllowed && !isAllowable

                  const bgColor = isAllowed
                    ? (theme === 'dark' ? 'bg-green-900 bg-opacity-20 border-green-800' : 'bg-green-50 border-green-200')
                    : isAllowable
                    ? (theme === 'dark' ? 'bg-blue-900 bg-opacity-20 border-blue-800' : 'bg-blue-50 border-blue-200')
                    : (theme === 'dark' ? 'bg-red-900 bg-opacity-20 border-red-800' : 'bg-red-50 border-red-200')
                  const textColor = isAllowed
                    ? (theme === 'dark' ? 'text-green-300' : 'text-green-700')
                    : isAllowable
                    ? (theme === 'dark' ? 'text-blue-300' : 'text-blue-700')
                    : (theme === 'dark' ? 'text-red-300' : 'text-red-700')
                  const secondaryTextColor = isAllowed
                    ? (theme === 'dark' ? 'text-green-200' : 'text-green-800')
                    : isAllowable
                    ? (theme === 'dark' ? 'text-blue-200' : 'text-blue-800')
                    : (theme === 'dark' ? 'text-red-200' : 'text-red-800')
                  const tertiaryTextColor = isAllowed
                    ? (theme === 'dark' ? 'text-green-100' : 'text-green-900')
                    : isAllowable
                    ? (theme === 'dark' ? 'text-blue-100' : 'text-blue-900')
                    : (theme === 'dark' ? 'text-red-100' : 'text-red-900')

                  return (
                  <div key={claimNum} className={`p-3 rounded-lg border ${bgColor}`}>
                    <div className={`font-medium mb-2 flex items-center gap-2 ${textColor}`}>
                      {isAllowed ? <FaCheckCircle /> : isAllowable ? <FaCheckCircle /> : <FaExclamationTriangle />}
                      Claim {claimNum.replace('claim_', '')}
                      {claimData.status && ` - ${claimData.status}`}
                      {claimData.rejection_type && ` (${claimData.rejection_type})`}
                    </div>

                    {claimData.notes && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        {claimData.notes}
                      </div>
                    )}

                    {claimData.issue && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Issue:</strong> {claimData.issue}
                      </div>
                    )}

                    {claimData.specific_problem && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Specific Problem:</strong> {claimData.specific_problem}
                      </div>
                    )}

                    {claimData.objection_type && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Objection Type:</strong> {claimData.objection_type}
                      </div>
                    )}

                    {claimData.objection_issue && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Objection Issue:</strong> {claimData.objection_issue}
                      </div>
                    )}

                    {claimData.objection_citation && (
                      <div className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        <strong>Citation:</strong> {claimData.objection_citation}
                      </div>
                    )}

                    {claimData.rejection_issue && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Rejection Issue:</strong> {claimData.rejection_issue}
                      </div>
                    )}

                    {claimData.prior_art_basis && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Prior Art Basis:</strong> {claimData.prior_art_basis}
                        {renderPriorArtButton(claimData.prior_art_basis)}
                      </div>
                    )}

                    {claimData.examiner_reasoning && (
                      <div className={`text-sm mb-2 ${tertiaryTextColor}`}>
                        <strong>Examiner Reasoning:</strong> {claimData.examiner_reasoning}
                      </div>
                    )}

                    {claimData.abstract_idea && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Abstract Idea:</strong> {claimData.abstract_idea}
                      </div>
                    )}

                    {claimData.judicial_exception && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Judicial Exception:</strong> {claimData.judicial_exception}
                      </div>
                    )}

                    {claimData.integration_analysis && (
                      <div className={`text-sm mb-2 ${secondaryTextColor}`}>
                        <strong>Integration Analysis:</strong> {claimData.integration_analysis}
                      </div>
                    )}

                    {claimData.claim_elements && claimData.claim_elements.length > 0 && (
                      <div className="mt-3">
                        <div className={`text-sm font-medium mb-2 ${secondaryTextColor}`}>
                          Claim Elements:
                        </div>
                        <div className="space-y-2">
                          {claimData.claim_elements.map((element, idx) => (
                            <div key={idx} className={`p-2 rounded-md ${isAllowed
                              ? (theme === 'dark' ? 'bg-green-800 bg-opacity-30' : 'bg-green-100')
                              : isAllowable
                              ? (theme === 'dark' ? 'bg-blue-800 bg-opacity-30' : 'bg-blue-100')
                              : (theme === 'dark' ? 'bg-red-800 bg-opacity-30' : 'bg-red-100')}`}>
                              <div className={`text-sm font-medium ${secondaryTextColor}`}>
                                {element.element}
                              </div>
                              <div className={`text-xs mt-1 ${isAllowed
                                ? (theme === 'dark' ? 'text-green-300' : 'text-green-700')
                                : isAllowable
                                ? (theme === 'dark' ? 'text-blue-300' : 'text-blue-700')
                                : (theme === 'dark' ? 'text-red-300' : 'text-red-700')}`}>
                                Found in: {element.found_in}
                              </div>
                              {element.notes && (
                                <div className={`text-xs mt-1 ${isAllowed
                                  ? (theme === 'dark' ? 'text-green-400' : 'text-green-600')
                                  : isAllowable
                                  ? (theme === 'dark' ? 'text-blue-400' : 'text-blue-600')
                                  : (theme === 'dark' ? 'text-red-400' : 'text-red-600')}`}>
                                  {element.notes}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {claimData.response_options && claimData.response_options.length > 0 && (
                      <div className="mt-3">
                        <div className={`text-sm font-medium mb-2 ${secondaryTextColor}`}>
                          Response Options:
                        </div>
                        <ul className="space-y-1">
                          {claimData.response_options.map((option, idx) => (
                            <li key={idx} className={`text-sm flex items-start gap-2 ${tertiaryTextColor}`}>
                              <FaLightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              {option}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Rejection Analysis */}
          {parsedResult.rejection_analysis && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaChartLine />
                Rejection Analysis
              </div>
              <div className="space-y-3">
                {Object.entries(parsedResult.rejection_analysis).map(([rejectionType, rejectionData]: [string, any]) => {
                  // Extract the base rejection type without the claim numbers in the key
                  // Keys look like: "103_rejection_claims_1_2_3_4_5" or "112_rejection_claims_6"
                  const baseRejectionType = rejectionType
                    .replace(/_claims?(_\d+)+$/i, '')  // Remove _claims_1_2_3... or _claim_6
                    .replace(/_/g, ' ')
                    .toUpperCase()
                    .trim()

                  return (
                  <div key={rejectionType} className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-orange-900 bg-opacity-20 border-orange-800' : 'bg-orange-50 border-orange-200'} border`}>
                    <div className={`font-medium mb-2 ${theme === 'dark' ? 'text-orange-300' : 'text-orange-700'}`}>
                      {baseRejectionType}
                      {rejectionData.claims_affected && ` (Claims ${rejectionData.claims_affected.join(', ')})`}
                    </div>

                    {rejectionData.rejection_type && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                        <strong>Type:</strong> {rejectionData.rejection_type}
                      </div>
                    )}

                    {rejectionData.objection_type && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                        <strong>Objection Type:</strong> {rejectionData.objection_type}
                      </div>
                    )}

                    {rejectionData.strength && (
                      <div className={`text-sm mb-2`}>
                        <strong>Strength:</strong> <span className={formatStrengthColor(rejectionData.strength)}>{rejectionData.strength}</span>
                      </div>
                    )}

                    {rejectionData.issue && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                        <strong>Issue:</strong> {rejectionData.issue}
                      </div>
                    )}

                    {rejectionData.specific_issues && rejectionData.specific_issues.length > 0 && (
                      <div className="mb-2">
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                          Specific Issues:
                        </div>
                        <ul className="space-y-1">
                          {rejectionData.specific_issues.map((issue, idx) => (
                            <li key={idx} className={`text-sm ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                              • {issue}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {rejectionData.specific_problems && rejectionData.specific_problems.length > 0 && (
                      <div className="mb-2">
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                          Specific Problems:
                        </div>
                        <ul className="space-y-1">
                          {rejectionData.specific_problems.map((problem, idx) => (
                            <li key={idx} className={`text-sm ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                              • {problem}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {rejectionData.primary_reference && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                        <strong>Primary Reference:</strong> {rejectionData.primary_reference}
                        {renderPriorArtButton(rejectionData.primary_reference)}
                      </div>
                    )}

                    {rejectionData.secondary_reference && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                        <strong>Secondary Reference:</strong> {rejectionData.secondary_reference}
                        {renderPriorArtButton(rejectionData.secondary_reference)}
                      </div>
                    )}

                    {rejectionData.secondary_references && rejectionData.secondary_references.length > 0 && (
                      <div className="mb-2">
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                          Secondary References:
                        </div>
                        <ul className="space-y-1">
                          {rejectionData.secondary_references.map((ref, idx) => (
                            <li key={idx} className={`text-sm ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                              • {ref}
                              {renderPriorArtButton(ref)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {rejectionData.combination_rationale && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                        <strong>Combination Rationale:</strong> {rejectionData.combination_rationale}
                      </div>
                    )}

                    {rejectionData.resolution && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                        <strong>Resolution:</strong> {rejectionData.resolution}
                      </div>
                    )}

                    {rejectionData.response_strategy && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                        <strong>Response Strategy:</strong> {rejectionData.response_strategy}
                      </div>
                    )}

                    {rejectionData.alice_mayo_analysis && (
                      <div className="mt-2 p-2 rounded-md bg-opacity-50 bg-gray-800">
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                          Alice/Mayo Analysis:
                        </div>
                        {rejectionData.alice_mayo_analysis.step_1 && (
                          <div className={`text-xs mb-1 ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                            <strong>Step 1:</strong> {rejectionData.alice_mayo_analysis.step_1}
                          </div>
                        )}
                        {rejectionData.alice_mayo_analysis.step_2A && (
                          <div className={`text-xs mb-1 ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                            <strong>Step 2A:</strong> {rejectionData.alice_mayo_analysis.step_2A}
                          </div>
                        )}
                        {rejectionData.alice_mayo_analysis.step_2B && (
                          <div className={`text-xs ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                            <strong>Step 2B:</strong> {rejectionData.alice_mayo_analysis.step_2B}
                          </div>
                        )}
                      </div>
                    )}

                    {rejectionData.weaknesses && rejectionData.weaknesses.length > 0 && (
                      <div className="mt-2">
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-orange-200' : 'text-orange-800'}`}>
                          Weaknesses:
                        </div>
                        <ul className="space-y-1">
                          {rejectionData.weaknesses.map((weakness, idx) => (
                            <li key={idx} className={`text-sm flex items-start gap-2 ${theme === 'dark' ? 'text-orange-100' : 'text-orange-900'}`}>
                              <FaExclamationTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Prior Art Analysis */}
          {parsedResult.prior_art_analysis && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaFileAlt />
                Prior Art Analysis
              </div>
              <div className="space-y-3">
                {Object.entries(parsedResult.prior_art_analysis).map(([refName, refData]: [string, any]) => (
                  <div key={refName} className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-blue-900 bg-opacity-20 border-blue-800' : 'bg-blue-50 border-blue-200'} border`}>
                    <div className={`font-medium mb-2 flex items-center justify-between ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                      <span>{cleanAuthorName(refName)}</span>
                      {refData.date && (
                        <span className={`text-xs ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                          {refData.date}
                        </span>
                      )}
                    </div>
                    
                    {renderPriorArtButton(refName)}
                    
                    {refData.relevance && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                        <strong>Relevance:</strong> {refData.relevance}
                      </div>
                    )}
                    
                    {refData.key_disclosures && refData.key_disclosures.length > 0 && (
                      <div className="mb-2">
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                          Key Disclosures:
                        </div>
                        <ul className="space-y-1">
                          {refData.key_disclosures.map((disclosure, idx) => (
                            <li key={idx} className={`text-sm ${theme === 'dark' ? 'text-blue-100' : 'text-blue-900'}`}>
                              • {disclosure}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {refData.distinguishing_features && refData.distinguishing_features.length > 0 && (
                      <div className="mb-2">
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                          Distinguishing Features:
                        </div>
                        <ul className="space-y-1">
                          {refData.distinguishing_features.map((feature, idx) => (
                            <li key={idx} className={`text-sm ${theme === 'dark' ? 'text-blue-100' : 'text-blue-900'}`}>
                              • {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {refData.potential_arguments && refData.potential_arguments.length > 0 && (
                      <div>
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                          Potential Arguments:
                        </div>
                        <ul className="space-y-1">
                          {refData.potential_arguments.map((argument, idx) => (
                            <li key={idx} className={`text-sm flex items-start gap-2 ${theme === 'dark' ? 'text-blue-100' : 'text-blue-900'}`}>
                              <FaLightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              {argument}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Response Strategy */}
          {parsedResult.response_strategy && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaClock />
                Response Strategy
              </div>
              <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-green-900 bg-opacity-20 border-green-800' : 'bg-green-50 border-green-200'} border`}>
                
                {parsedResult.response_strategy.primary_approach && (
                  <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                    <strong>Primary Approach:</strong> {parsedResult.response_strategy.primary_approach}
                  </div>
                )}
                
                {parsedResult.response_strategy.timeline && (
                  <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                    <strong>Timeline:</strong> {parsedResult.response_strategy.timeline}
                  </div>
                )}
                
                {parsedResult.response_strategy.priorities && parsedResult.response_strategy.priorities.length > 0 && (
                  <div>
                    <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                      Priorities:
                    </div>
                    <ul className="space-y-1">
                      {parsedResult.response_strategy.priorities.map((priority, idx) => (
                        <li key={idx} className={`text-sm flex items-start gap-2 ${theme === 'dark' ? 'text-green-100' : 'text-green-900'}`}>
                          <FaCheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {priority}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Amendment Suggestions */}
          {parsedResult.amendment_suggestions && parsedResult.amendment_suggestions.length > 0 && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaEdit />
                Amendment Suggestions
              </div>
              <div className="space-y-3">
                {parsedResult.amendment_suggestions.map((amendment, idx) => (
                  <div key={idx} className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-purple-900 bg-opacity-20 border-purple-800' : 'bg-purple-50 border-purple-200'} border`}>
                    <div className={`font-medium mb-2 ${theme === 'dark' ? 'text-purple-300' : 'text-purple-700'}`}>
                      Claim {amendment.claim} - {amendment.type}
                    </div>
                    
                    {amendment.suggestion && (
                      <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-purple-200' : 'text-purple-800'}`}>
                        <strong>Suggestion:</strong> {amendment.suggestion}
                      </div>
                    )}
                    
                    {amendment.rationale && (
                      <div className={`text-sm ${theme === 'dark' ? 'text-purple-100' : 'text-purple-900'}`}>
                        <strong>Rationale:</strong> {amendment.rationale}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Argument Points */}
          {parsedResult.argument_points && parsedResult.argument_points.length > 0 && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaLightbulb />
                Argument Points
              </div>
              <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-yellow-900 bg-opacity-20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'} border`}>
                <ul className="space-y-2">
                  {parsedResult.argument_points.map((point, idx) => (
                    <li key={idx} className={`text-sm flex items-start gap-2 ${theme === 'dark' ? 'text-yellow-100' : 'text-yellow-900'}`}>
                      <FaLightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Strength Assessment */}
          {parsedResult.strength_assessment && (
            <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <FaChartLine />
                Strength Assessment
              </div>
              <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                
                {parsedResult.strength_assessment.overall_rejection_strength && (
                  <div className={`text-sm mb-2`}>
                    <strong>Overall Rejection Strength:</strong> 
                    <span className={`ml-1 ${formatStrengthColor(parsedResult.strength_assessment.overall_rejection_strength)}`}>
                      {parsedResult.strength_assessment.overall_rejection_strength}
                    </span>
                  </div>
                )}
                
                {parsedResult.strength_assessment.winnable_issues && parsedResult.strength_assessment.winnable_issues.length > 0 && (
                  <div className="mb-2">
                    <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Winnable Issues:
                    </div>
                    <ul className="space-y-1">
                      {parsedResult.strength_assessment.winnable_issues.map((issue, idx) => (
                        <li key={idx} className={`text-sm flex items-start gap-2 ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                          <FaCheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {parsedResult.strength_assessment.difficult_issues && parsedResult.strength_assessment.difficult_issues.length > 0 && (
                  <div className="mb-2">
                    <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Difficult Issues:
                    </div>
                    <ul className="space-y-1">
                      {parsedResult.strength_assessment.difficult_issues.map((issue, idx) => (
                        <li key={idx} className={`text-sm flex items-start gap-2 ${theme === 'dark' ? 'text-red-200' : 'text-red-800'}`}>
                          <FaExclamationTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {parsedResult.strength_assessment.recommended_action && (
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                    <strong>Recommended Action:</strong> {parsedResult.strength_assessment.recommended_action}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  )
}

export default OfficeActionAnalysisDisplay